import { useCallback, useMemo, useRef } from "react";
import useSWR from "swr";
import { expireLiveMapRecords, mergeLiveMapFeed } from "./liveMapFeed.js";

/** Shared SWR ownership for non-vehicle live map feeds such as scooters. */
export function useLiveMapFeed({
  enabled,
  endpoint,
  refreshInterval = 20000,
  staleAfterMs = 90000,
  buildFeatureCollection
}) {
  const latestRecords = useRef({});
  const fetchFeed = useCallback(async url => {
    const response = await fetch(url, {
      cache: "no-store"
    });
    if (!response.ok) throw new Error(`Failed to load live map data: ${response.status}`);
    const payload = await response.json();
    const records = mergeLiveMapFeed(latestRecords.current, payload, {
      defaultStaleAfterMs: staleAfterMs
    });
    latestRecords.current = records;
    return records;
  }, [staleAfterMs]);
  const {
    data,
    error,
    isLoading,
    mutate: refresh
  } = useSWR(enabled ? endpoint : null, fetchFeed, {
    refreshInterval,
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    refreshWhenHidden: false,
    refreshWhenOffline: false,
    dedupingInterval: Math.min(refreshInterval, 10000)
  });
  // Switching the SWR key to null while hidden makes `data` unavailable on
  // the first render after it is enabled again. Keep the last fresh snapshot
  // visible during that revalidation so its MapLibre source is not recreated
  // empty; expiry still prevents an old snapshot being reused indefinitely.
  const visibleRecords = data || expireLiveMapRecords(latestRecords.current, {
    staleAfterMs
  });
  return useMemo(() => ({
    records: visibleRecords,
    featureCollection: buildFeatureCollection(visibleRecords),
    error,
    isLoading,
    refresh
  }), [buildFeatureCollection, error, isLoading, refresh, visibleRecords]);
}